import { useEffect, useMemo, useRef, useState } from "react";
import { loadPdf, extractPageText, isPdfSource } from "../../utils/pdfRender";
import { isBlobPointer } from "../../utils/blobStorage";
import { buildEntries, searchEntries, snippet } from "../../utils/planSearch";

// Search every drawing's printed text and copy a sheet's text. Reads each PDF's
// text layer in this browser (nothing is uploaded) the first time the panel opens
// and keeps the result for the session. A scanned sheet has no text layer — it is
// listed as unsearchable instead of silently skipped (there is no OCR).

const INDEX_CACHE = new Map(); // sheet signature -> { entries, pages, lines }
const sigOf = (s) => `${s.SheetID}:${+s.ImageRotation || 0}:${(s.BackgroundImage || "").length}:${(s.BackgroundImage || "").slice(-48)}`;
export const sheetLabelOf = (s) => s.SheetNumber || s.SheetName || `Sheet ${s.SheetID}`;

async function indexSheet(sheet, shouldStop) {
  const { doc, pageSizes } = await loadPdf(sheet.BackgroundImage, { page: sheet.ImagePage || 1, rotation: sheet.ImageRotation });
  const entries = [];
  let lines = 0;
  // a sheet in a stored set is ONE page of a big file — search only that page (reading all of them would download the set)
  const pages = isBlobPointer(sheet.BackgroundImage) ? [sheet.ImagePage || 1] : Array.from({ length: pageSizes.length }, (_, i) => i + 1);
  try {
    for (const p of pages) {
      if (shouldStop()) break;
      const { items, page } = await extractPageText(doc, p, sheet.ImageRotation);
      const e = buildEntries({ sheetId: sheet.SheetID, label: sheetLabelOf(sheet), page: p, items, pageSize: page });
      lines += e.length;
      entries.push(...e);
    }
  } finally {
    if (!doc.__shared) { try { doc.destroy?.(); } catch { /* already gone */ } }   // a stored set's document is shared by its other sheets
  }
  return { entries, pages: pageSizes.length, lines };
}

export default function PlanSearchPanel({ sheets, activeSheetId, activePage, onGo, onClose }) {
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(() => new Map()); // SheetID -> { entries, pages, lines }
  const [progress, setProgress] = useState({ done: 0, total: 0, busy: false });
  const [note, setNote] = useState("");
  const inputRef = useRef(null);

  const pdfSheets = useMemo(() => sheets.filter(s => s.ImageIsPdf && isPdfSource(s.BackgroundImage)), [sheets]);
  const imageSheets = sheets.filter(s => s.BackgroundImage && !(s.ImageIsPdf && isPdfSource(s.BackgroundImage)));

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Read each drawing once, active sheet first so its results appear first.
  useEffect(() => {
    let stop = false;
    const todo = [...pdfSheets].sort((a, b) => (b.SheetID === activeSheetId) - (a.SheetID === activeSheetId));
    setProgress({ done: 0, total: todo.length, busy: todo.length > 0 });
    (async () => {
      let done = 0;
      for (const s of todo) {
        if (stop) return;
        const sig = sigOf(s);
        let res = INDEX_CACHE.get(sig);
        if (!res) {
          try { res = await indexSheet(s, () => stop); } catch (e) { console.error("Plan search: could not read", sheetLabelOf(s), e); res = { entries: [], pages: 0, lines: 0, failed: true }; }
          if (stop) return;
          INDEX_CACHE.set(sig, res);
        }
        done += 1;
        setIndex(prev => new Map(prev).set(s.SheetID, res));
        setProgress({ done, total: todo.length, busy: done < todo.length });
      }
    })();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfSheets.map(sigOf).join("|")]);

  const entries = useMemo(() => [...index.values()].flatMap(r => r.entries), [index]);
  const hits = useMemo(() => searchEntries(entries, query, { limit: 100 }), [entries, query]);
  const noText = pdfSheets.filter(s => index.has(s.SheetID) && index.get(s.SheetID).lines === 0);

  async function copySheetText() {
    const mine = (index.get(activeSheetId)?.entries || []).filter(e => e.page === (activePage || 1));
    if (!mine.length) { setNote("This page has no text layer to copy."); return; }
    const text = mine.map(e => e.text).join("\n");
    try { await navigator.clipboard.writeText(text); setNote(`Copied ${mine.length} lines.`); }
    catch { setNote("The browser blocked copying — select the text in the results instead."); }
  }

  return (
    <div role="dialog" aria-label="Search drawings" style={{ position: "absolute", top: 8, left: 8, zIndex: 25, width: 380, maxHeight: "70%", display: "flex", flexDirection: "column", background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.22)", fontSize: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", borderBottom: "1px solid var(--cs-border)" }}>
        <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} placeholder="Search every drawing's text (e.g. HEPA, D-101, 2x4)…" aria-label="Search text"
          style={{ flex: 1, height: 28, padding: "0 8px", border: "1px solid var(--cs-border)", borderRadius: 6, fontSize: 12.5, background: "var(--surface)", color: "var(--text)" }} />
        <button type="button" onClick={onClose} aria-label="Close search" style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 16, color: "var(--text3)" }}>✕</button>
      </div>

      <div style={{ padding: "6px 10px", color: "var(--text3)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {progress.busy ? <span>Reading drawings… {progress.done} of {progress.total}</span> : <span>{pdfSheets.length} drawing{pdfSheets.length === 1 ? "" : "s"} searched</span>}
        <button type="button" onClick={copySheetText} style={{ marginLeft: "auto", fontSize: 11, padding: "2px 8px", border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", cursor: "pointer", color: "var(--text2)" }}
          title="Copy this drawing page's text in reading order">Copy this page's text</button>
      </div>
      {note && <div style={{ padding: "0 10px 6px", color: "var(--text3)" }}>{note}</div>}

      <div style={{ overflowY: "auto", padding: "0 4px 6px" }}>
        {query.trim() && !progress.busy && hits.length === 0 && <div style={{ padding: "8px 10px", color: "var(--text3)" }}>No drawing text matches “{query.trim()}”.</div>}
        {hits.map((h, i) => {
          const sn = snippet(h);
          return (
            <button key={`${h.entry.sheetId}-${h.entry.page}-${h.start}-${i}`} type="button" onClick={() => onGo?.(h)}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 8px", border: "none", background: "transparent", cursor: "pointer", borderRadius: 6, color: "var(--text)" }}
              onMouseEnter={e => { e.currentTarget.style.background = "var(--bg, rgba(0,0,0,0.05))"; }} onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)" }}>{h.entry.label}{index.get(h.entry.sheetId)?.pages > 1 ? ` · page ${h.entry.page}` : ""}</div>
              <div style={{ fontFamily: "monospace", fontSize: 11.5, whiteSpace: "normal", wordBreak: "break-word" }}>
                {sn.before}<mark style={{ background: "#fde68a", color: "inherit", padding: 0 }}>{sn.match}</mark>{sn.after}
              </div>
            </button>
          );
        })}
        {hits.length === 100 && <div style={{ padding: "4px 10px", color: "var(--text3)" }}>Showing the first 100 — refine the search.</div>}
      </div>

      {(noText.length > 0 || imageSheets.length > 0) && (
        <div style={{ padding: "6px 10px", borderTop: "1px solid var(--cs-border)", color: "#92400e", background: "var(--tint-amber)", borderRadius: "0 0 10px 10px" }}>
          {noText.length > 0 && <div>No text layer (scanned), not searchable: {noText.map(sheetLabelOf).join(", ")}.</div>}
          {imageSheets.length > 0 && <div>Image drawings, not searchable: {imageSheets.map(sheetLabelOf).join(", ")}.</div>}
        </div>
      )}
    </div>
  );
}
